import { countableProviders, providerKey } from "@/lib/named-providers";
import { answerNames } from "@/lib/briefing-matrix";
import { answerNamesACompany, isBrandedPrompt, textNamesBrand, untrackedBusinessNames } from "@/lib/brand-placement";

// Shared "latest scan" view used by Mission Control's headline, featured
// answer and Stage Gap Grid, so all three read the exact same answers.
// For each current question we keep only the answers from that question's
// newest scan batch. A partial rerun on a few questions no longer hides the
// rest of the questions' most recent answers.

/** How far back every "latest scan" reader looks. One value so the cards,
 *  headline, ranking, grid and jobs always read the same answers. Long enough
 *  for monthly free scans. */
export const LATEST_SCAN_DAYS = 45;
export const latestScanSince = () => new Date(Date.now() - LATEST_SCAN_DAYS * 86400000).toISOString();

type ScanRow = { prompt_id: string | null; created_at: string; scan_batch_id: string | null };

export function latestAnswersByPrompt<T extends ScanRow>(rows: T[], activeIds: Set<string>): Map<string, T[]> {
  const sorted = [...rows].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const out = new Map<string, T[]>();
  const pick = new Map<string, { batch: string | null; at: number }>();
  for (const r of sorted) {
    const pid = r.prompt_id;
    if (!pid || !activeIds.has(pid)) continue;
    const t = new Date(r.created_at).getTime();
    const chosen = pick.get(pid);
    if (!chosen) {
      pick.set(pid, { batch: r.scan_batch_id, at: t });
      out.set(pid, [r]);
      continue;
    }
    const same = chosen.batch ? r.scan_batch_id === chosen.batch : chosen.at - t < 3600000;
    if (same) out.get(pid)!.push(r);
  }
  return out;
}

export const ALL_ASSISTANTS = ["chatgpt", "claude", "gemini", "perplexity", "google_aio", "google_ai_mode"] as const;


/**
 * Did this stored answer name the business? Stored mention metadata only
 * counts when one of its matched strings appears as a whole word in the
 * answer (so "Jen" inside "Jennifer" no longer counts as Jen Realty).
 */
export function answerNamesBusiness(
  row: { mentioned_entities: unknown; response_text: string | null },
  name: string,
  names: string[],
): boolean {
  const text = row.response_text ?? "";
  if (textNamesBrand(text, names)) return true;
  if (!answerNames(row.mentioned_entities, name)) return false;
  if (!text) return true;
  const list = Array.isArray(row.mentioned_entities) ? (row.mentioned_entities as any[]) : [];
  const hit = list.find((m) => String(m?.name ?? "").toLowerCase() === name.toLowerCase());
  const strings: string[] = Array.isArray(hit?.matched_strings) ? hit.matched_strings : [];
  return strings.length === 0 || textNamesBrand(text, strings);
}

/**
 * THE shared visibility metric. Every page (Mission Control headline, Stage
 * Gap Grid, Competitors) counts the same way:
 *  - question set: current, non-informational, non-branded questions;
 *  - answers: each question's newest scan batch only;
 *  - a question counts as "named" when any of its latest answers names the
 *    business (answerNamesBusiness).
 */
export function scoredPrompts<P extends { prompt_text: string; informational_at?: string | null }>(
  prompts: P[],
  ownNames: string[],
): P[] {
  return prompts.filter((p) => !p.informational_at && !isBrandedPrompt(p.prompt_text, ownNames));
}

export type QuestionCell = { count: number; total: number; rate: number };

export function questionsNaming<T extends { mentioned_entities: unknown; response_text: string | null }>(
  byPrompt: Map<string, T[]>,
  promptIds: Iterable<string>,
  name: string,
  names: string[],
): QuestionCell {
  let total = 0;
  let count = 0;
  for (const id of promptIds) {
    const answers = byPrompt.get(id);
    if (!answers?.length) continue;
    total++;
    if (answers.some((r) => answerNamesBusiness(r, name, names))) count++;
  }
  return { count, total, rate: total ? count / total : 0 };
}

export type FramingEntity = { name: string; names: string[] };

export type ScanFraming = {
  /** Scored questions with answers in the latest scan. */
  asked: number;
  ownNamed: Set<string>;
  /** Questions where at least one tracked competitor is named. */
  competitorNamed: Set<string>;
  /** Questions where no business at all is named (answerNamesACompany false). */
  open: Set<string>;
  /** Questions where only an untracked business is named: "Someone else wins". */
  elsewhere: Set<string>;
  /** Questions with no competitor named: open ones plus ones only you won. */
  winnable: Set<string>;
  /** Per question: how many distinct competitors were named. */
  competitorCount: Map<string, number>;
};

/**
 * New-account framing, from the same per-question latest answers and name
 * matching as questionsNaming. Every surface (headline, jobs) uses this.
 */
export function scanFraming<T extends { mentioned_entities: unknown; response_text: string | null }>(
  byPrompt: Map<string, T[]>,
  promptIds: Iterable<string>,
  own: FramingEntity,
  competitors: FramingEntity[],
): ScanFraming {
  const out: ScanFraming = {
    asked: 0,
    ownNamed: new Set(),
    competitorNamed: new Set(),
    open: new Set(),
    elsewhere: new Set(),
    winnable: new Set(),
    competitorCount: new Map(),
  };
  for (const id of promptIds) {
    const answers = byPrompt.get(id);
    if (!answers?.length) continue;
    out.asked++;
    const mine = answers.some((r) => answerNamesBusiness(r, own.name, own.names));
    const comps = competitors.filter((c) => answers.some((r) => answerNamesBusiness(r, c.name, c.names))).length;
    out.competitorCount.set(id, comps);
    if (mine) out.ownNamed.add(id);
    if (comps > 0) out.competitorNamed.add(id);
    else out.winnable.add(id);
    if (!mine && comps === 0) {
      if (answers.some((r) => answerNamesAnyBusiness(r))) out.elsewhere.add(id);
      else out.open.add(id);
    }
  }
  return out;
}

/** Days since the account was created (auth user created_at). */
export function accountAgeDays(createdAt: string | null | undefined): number {
  if (!createdAt) return 9999;
  return Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);
}

/** Existing any-business check, applied to one stored answer. */
export function answerNamesAnyBusiness(row: { mentioned_entities: unknown; response_text: string | null; cited_sources?: unknown; named_providers?: unknown }): boolean {
  if (Array.isArray(row.named_providers)) return countableProviders(row.named_providers).length > 0;
  return answerNamesACompany(row.response_text ?? "", row.mentioned_entities, row.cited_sources ?? null);
}

/** Untracked business names across a question's latest answers. */
export function untrackedNamesIn(answers: Array<{ response_text: string | null; named_providers?: unknown }>, known: string[]): string[] {
  if (answers.some((a) => Array.isArray(a.named_providers))) {
    const knownKeys = new Set(known.map(providerKey));
    const out = new Map<string, string>();
    for (const a of answers) for (const p of countableProviders(a.named_providers)) {
      const k = providerKey(p.name);
      if (k && !knownKeys.has(k) && !out.has(k)) out.set(k, p.name);
    }
    return [...out.values()];
  }
  return Array.from(new Set(answers.flatMap((a) => untrackedBusinessNames(a.response_text ?? "", known))));
}
